import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Breadcrumb, Button, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { IManager } from '../../commons/interfaces/auth.interface';
import { IChatbotAPIPagination } from '../../commons/interfaces/chatbotAPI.interface';
import CustomTable from '../../components/CustomTable';
import { ICustomTableCellProps } from '../../components/CustomTableCell/interfaces';
import { ICustomTableFooterProps } from '../../components/CustomTableFooter/interfaces';
import Paper from '../../components/Paper';
import useAuth from '../../hooks/auth.hook';
import ManagerAPI from '../../services/manager.service';
import './style.scss';
import tableColumnMetadata from './table.metadata';
import CustomModal from '../../components/CustomModal';
import { CustomModalProps } from '../../components/CustomModal/interfaces';
import ManagerForm from './ManagerForm';
import { IManagerRequest } from '../../commons/interfaces/manager.interface';
import useAPIError from '../../hooks/apiError.context';
import { ActionType } from '../../components/CustomTable/interfaces';

const managerAPI = new ManagerAPI();

const Managers = () => {
  const { user, refresh } = useAuth();
  const { showError } = useAPIError();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const initialPagination = { page: 1, pageSize: 10, pageCount: 1, totalCount: 1 };
  const initialCurrentManager: Partial<IManagerRequest> = {
    role: 'MANAGER',
    displayedRole: 'Usuario'
  };
  const [managers, setManagers] = useState<IManager[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IChatbotAPIPagination>(initialPagination);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentManager, setCurrentManager] = useState<IManagerRequest>(initialCurrentManager);
  const [formValidated, setFormValidated] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ActionType>('CREATE');

  useEffect(() => {
    const fetchManagers = async () => {
      if (user?.role === 'ADMIN') {
        setLoading(true);

        const response = await managerAPI.listManagers(page);

        setManagers(response.data);
        setPagination(response.pagination);
        setLoading(false);
      }
    };

    if (user?.role === 'MANAGER') {
      navigate('../businesses');
    }

    fetchManagers();
  }, [page, user?.role, navigate]);

  const handleModalShow = () => {
    setShowModal(true);
  };

  const handleModalHide = () => {
    setShowModal(false);
    setFormValidated(false);
    setCurrentManager(initialCurrentManager);
  };

  const handleCreateClick = () => {
    setModalType('CREATE');
    handleModalShow();
  };

  const validateForm = () => {
    const form = formRef.current;
    const isFormValid = form?.checkValidity();

    return isFormValid;
  };

  const handleCreateManager = async () => {
    if (validateForm()) {
      try {
        const newManagerData = {
          name: currentManager.name,
          email: currentManager.email,
          role: currentManager.role,
          withNotification: currentManager.withNotification
        };

        const response = await managerAPI.createManager(newManagerData);

        const canRenderNewManager = managers.length < pagination.pageSize;

        if (canRenderNewManager) {
          setManagers([...managers, response.data]);
          setPagination({ ...pagination, totalCount: pagination.totalCount + 1 });
        }

        handleModalHide();
      } catch (err) {
        showError(err as Error);
        setFormValidated(false);
      }
    }
  };

  const handleEditManager = async () => {
    if (validateForm()) {
      try {
        const managerFound = _.find(managers, (manager) => manager.id === currentManager.id);

        if (managerFound !== currentManager) {
          const { id: managerId, role, isBlocked, newAdminEmail, name } = currentManager;

          const newManagerData: any = {
            name,
            isBlocked
          };

          if (role !== managerFound?.role) {
            newManagerData.role = role;
            if (managerFound?.role === 'ADMIN' && role === 'MANAGER') {
              newManagerData.newAdminEmail = newAdminEmail;
            }
          }

          const response = await managerAPI.modifyManager(managerId as string, newManagerData);

          if (newManagerData.newAdminEmail) {
            await refresh();
          }

          setManagers(
            _.map(managers, (manager) =>
              manager.id === response.data.id ? response.data : manager
            )
          );
        }

        handleModalHide();
      } catch (err) {
        showError(err as Error);
        setFormValidated(false);
      }
    }
  };
  const handleDeleteManager = async () => {
    try {
      if (currentManager.id) {
        // TODO: Al eliminar debe buscar de nuevo a todos los managers para evitar bugs
        // BUG: Cuando se elimina un manager y no estas en la ultima pagina, puede generar bug
        await managerAPI.deleteManager(currentManager.id);
        setManagers(_.filter(managers, (manager) => manager.id !== currentManager.id));
        setPagination({ ...pagination, totalCount: pagination.totalCount - 1 });
      }

      handleModalHide();
    } catch (err) {
      showError(err as Error);
    }
  };

  const handleModalSubmit = async () => {
    setFormValidated(true);

    if (modalType === 'CREATE') {
      handleCreateManager();
    } else if (modalType === 'EDIT') {
      handleEditManager();
    } else if (modalType === 'DELETE') {
      handleDeleteManager();
    }
  };

  const handleActionClick: ICustomTableCellProps<IManager>['onClick'] = (item, type) => {
    const selectedManager = item as IManagerRequest;
    setCurrentManager(selectedManager);
    setModalType(type);
    handleModalShow();
  };

  const handleFooterClick = (value: number) => {
    setPage(page + value);
  };

  const footerProps: ICustomTableFooterProps = {
    onClick: handleFooterClick,
    pagination,
    loading
  };

  const modalProps: Omit<CustomModalProps, 'children'> = {
    show: showModal,
    title: 'usuario',
    type: modalType,
    onSubmit: handleModalSubmit,
    onHide: handleModalHide
  };

  return (
    <Container id="managers" fluid>
      <Breadcrumb className="breadcrum">
        <Breadcrumb.Item active>Gestión de usuarios</Breadcrumb.Item>
      </Breadcrumb>
      <Paper>
        <Row className="justify-content-space-between mb-4">
          <Col className="d-flex m-auto">
            <span>Gestión de usuarios</span>
          </Col>
          <Col xs="auto">
            <Button className="pill" onClick={handleCreateClick}>
              + Nuevo usuario
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <CustomTable<IManager>
              data={managers}
              columnsMetadata={tableColumnMetadata}
              footerProps={footerProps}
              loading={loading}
              onActionClick={handleActionClick}
            />
          </Col>
        </Row>
      </Paper>
      <CustomModal {...modalProps}>
        <ManagerForm
          validated={formValidated}
          formRef={formRef}
          currentManager={currentManager}
          setCurrentManager={setCurrentManager}
          actionType={modalType}
        />
      </CustomModal>
    </Container>
  );
};

export default Managers;
